import { MAX_TOOL_OUTPUT } from '@/utils';
import type { Message } from '../types';
import { Sender } from '../types';
import { MessageMedia } from '@/components/Conversations/MessageMedia';
import { StructuredMessage } from '@/components/StructuredMessage';
import { MessageSuggestionList, SuggestionClick } from '@/components/MessageSuggestion';
import {
  CarouselPart,
  SuggestionListPart,
  TextPart,
  ToolCallPart,
  ToolResultPart,
} from '@/sharedType';

export const MessageCard = ({
  message,
  showSources = false,
  showSuggestionList = false,
  detailed = false,
  customUserText,
  onSuggestionClick,
  customUserBackground,
}: {
  message: Message;
  showSources?: boolean;
  showSuggestionList?: boolean;
  onSuggestionClick?: SuggestionClick;
  detailed?: boolean;
  customUserText?: string;
  customUserBackground?: string;
}) => {
  const user = message.sender === Sender.User;
  let markdown: string = '';
  let toolResultList: ToolResultPart[] = [];
  let toolCallList: ToolCallPart[] = [];
  let markdownList: TextPart[] = [];
  let carouselPart: CarouselPart[] = [];
  let suggestionPart: SuggestionListPart | undefined = undefined;

  if (typeof message.content === 'string') {
    markdown = message.content;
  } else {
    toolResultList = message.content.filter((e) => e.type === 'tool-result');
    toolCallList = message.content.filter((e) => e.type === 'tool-call');
    suggestionPart = message.content.find((e) => e.type === 'suggestionList');
    carouselPart = message.content.filter((e) => e.type === 'carousel');
    markdownList = message.content.filter((e) => e.type === 'text');
  }

  const defaultBackground = user ? 'bg-violet-100' : 'bg-gray-100';

  if (detailed) {
    if (toolResultList.length) {
      toolResultList.forEach((e) => {
        const limitedValue =
          typeof e.output.value === 'string'
            ? e.output.value.slice(0, MAX_TOOL_OUTPUT)
            : e.output.value;

        markdown += `${e.toolName}() => '${limitedValue}'`;
      });
    }

    if (toolCallList.length) {
      toolCallList.forEach((e) => {
        markdown += `${e.toolName}(${JSON.stringify(e.input)})`;
      });
    }
  }

  const isMessageToShow = markdown || markdownList.length || carouselPart.length;
  if (!isMessageToShow && !suggestionPart?.list) {
    return null;
  }

  return (
    <>
      {isMessageToShow ? (
        <div
          style={user && customUserBackground ? { background: customUserBackground } : undefined}
          className={`flex flex-col gap-4 rounded-md ${user && customUserBackground ? undefined : defaultBackground}`}
        >
          {/* This is for the media that the user just uploaded and submitted */}
          <MessageMedia content={message.content} />

          <StructuredMessage
            isUser={user}
            customUserText={customUserText}
            markdown={markdown}
            markdownList={markdownList}
            carouselPart={carouselPart}
          />

          {showSources && message.sources && message.content.length > 125 && (
            <>
              <hr className="my-2" />
              <div className="space-y-1">
                {message.sources.map((source, index) => (
                  <div key={index} className="flex gap-2 items-center">
                    <span className="text-sm text-gray-600">{index + 1}</span>
                    <a
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-gray-600 truncate"
                    >
                      {source.title}
                    </a>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      ) : null}
      {showSuggestionList && suggestionPart && (
        <MessageSuggestionList
          onSuggestionClick={onSuggestionClick}
          suggestionList={suggestionPart.list}
        />
      )}
    </>
  );
};
